import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Button,
  Card,
  CardContent,
  useTheme,
  Fade
} from '@mui/material';
import {
  ArrowRight,
  Eye,
  Monitor,
  Camera,
  Smartphone,
  Play,
  Layers
} from 'lucide-react';

// Editorial Feature Card Component - Reusable Pattern
const FeatureCard = ({ icon: Icon, title, description, delay }) => {
  return (
    <Fade in={true} style={{ transitionDelay: `${delay}ms` }}>
      <Card 
        sx={{ 
          height: '100%', 
          textAlign: 'center',
          background: '#FFFFFF',
          border: '1px solid #E0E0E0',
          borderRadius: 0,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-8px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
            borderColor: '#FF5722'
          }
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              color: 'white',
              boxShadow: '0 8px 25px rgba(255,87,34,0.3)'
            }}
          >
            <Icon size={32} />
          </Box>
          
          <Typography 
            variant="h5" 
            component="h3" 
            color="#1A1A1A" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 2,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {title}
          </Typography>
          
          <Typography 
            variant="body1" 
            color="#666" 
            sx={{ 
              lineHeight: 1.6,
              fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
            }}
          >
            {description}
          </Typography>
        </CardContent>
      </Card>
    </Fade>
  );
};

const ThreeDVisualizationPage = () => {
  const [showElements, setShowElements] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setShowElements(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: Eye,
      title: 'Photorealistic Renders',
      description: 'Ultra-high quality 3D renders that bring your design vision to life before construction begins.'
    },
    {
      icon: Monitor,
      title: 'Virtual Walkthroughs',
      description: 'Interactive 360° virtual tours that let you explore every corner of your future space.'
    },
    {
      icon: Camera,
      title: 'Multiple Perspectives',
      description: 'Various viewpoints and angles to showcase different aspects of your design.'
    },
    {
      icon: Smartphone,
      title: 'VR Experience',
      description: 'Immersive virtual reality experiences for the most realistic preview possible.'
    },
    {
      icon: Play,
      title: 'Animation Videos',
      description: 'Dynamic video presentations that showcase your space in motion and different lighting conditions.'
    },
    {
      icon: Layers,
      title: 'Design Variations',
      description: 'Compare multiple design options side-by-side to make informed decisions.'
    }
  ];

  const benefits = [
    {
      title: 'Save Time & Money',
      description: 'Identify and resolve design issues before construction begins, avoiding costly changes later.',
      icon: '💰'
    },
    {
      title: 'Perfect Communication',
      description: 'Ensure everyone on your team understands the vision with crystal-clear visualizations.',
      icon: '💬'
    },
    {
      title: 'Confident Decisions',
      description: 'Make informed choices about materials, colors, and layouts with realistic previews.',
      icon: '✅'
    },
    {
      title: 'Client Approval',
      description: 'Present designs to stakeholders and clients with professional, impressive visuals.',
      icon: '👥'
    }
  ];

  const services = [
    {
      title: 'Architectural Visualization',
      description: 'Exterior and interior architectural renderings for residential and commercial projects.',
      image: '/images/architectural-viz.jpg'
    },
    {
      title: 'Interior Design Renders',
      description: 'Detailed interior visualizations showcasing furniture, lighting, and material selections.',
      image: '/images/interior-viz.jpg'
    },
    {
      title: 'Virtual Reality Tours',
      description: 'Immersive VR experiences that allow clients to walk through their future spaces.',
      image: '/images/vr-tours.jpg'
    }
  ];

  return (
    <Box sx={{ overflow: 'hidden', background: '#FAFAFA' }}>
      {/* Hero Section - Editorial Pattern */}
      <Box 
        sx={{ 
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          background: 'linear-gradient(135deg, #FAFAFA 0%, #FFFFFF 100%)',
          pt: { xs: 15, md: 20 },
          pb: { xs: 10, md: 15 },
          overflow: 'hidden'
        }}
      >
        {/* Background Geometric Elements */}
        <Box
          sx={{
            position: 'absolute',
            top: '10%',
            left: '5%',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, rgba(255,87,34,0.03), transparent)',
            filter: 'blur(60px)',
            zIndex: 0
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: '20%',
            right: '10%',
            width: 150,
            height: 150,
            background: 'linear-gradient(45deg, rgba(255,87,34,0.02), transparent)',
            transform: 'rotate(45deg)',
            zIndex: 0
          }}
        />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={showElements} timeout={1000}>
                <Box>
                  {/* Badge Component */}
                  <Box 
                    sx={{ 
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 3, 
                      py: 1, 
                      background: 'rgba(255,87,34,0.08)',
                      border: '1px solid rgba(255,87,34,0.2)',
                      borderRadius: 0,
                      mb: 4
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="#FF5722"
                      sx={{
                        fontWeight: 600,
                        letterSpacing: 2,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      3D Visualization
                    </Typography>
                  </Box>
                  
                  {/* Headline */}
                  <Typography 
                    variant="h1" 
                    component="h1" 
                    color="#1A1A1A"
                    sx={{ 
                      fontSize: { xs: '3rem', md: '4.5rem', lg: '5.5rem' },
                      fontWeight: 800,
                      lineHeight: 0.9,
                      mb: 3,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                    }}
                  >
                    See Your{' '}
                    <Box 
                      component="span" 
                      sx={{ 
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      Vision
                    </Box>
                    <Box component="span" display="block" mt={1}>
                      Come to Life
                    </Box>
                  </Typography>
                  
                  {/* Subtitle */}
                  <Typography 
                    variant="h6" 
                    component="p" 
                    color="#666"
                    sx={{ 
                      mb: 8,
                      lineHeight: 1.7,
                      fontSize: '1.2rem',
                      position: 'relative',
                      pl: 4,
                      fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: 8,
                        width: 3,
                        height: 40,
                        background: 'linear-gradient(to bottom, #FF5722, #FF8A65)'
                      }
                    }}
                  >
                    Experience your space before it's built with photorealistic 3D visualizations 
                    and immersive virtual reality tours.
                  </Typography>

                  {/* CTA Buttons */}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Button
                      component={RouterLink}
                      to="/contact"
                      variant="contained"
                      size="large"
                      endIcon={<ArrowRight />}
                      sx={{
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        color: 'white',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 700,
                        letterSpacing: 1.5,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        boxShadow: '0 8px 25px rgba(255,87,34,0.3)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-3px)',
                          boxShadow: '0 12px 35px rgba(255,87,34,0.4)'
                        }
                      }}
                    >
                      Request Visualization
                    </Button>
                    
                    <Button
                      component={RouterLink}
                      to="/portfolio"
                      variant="outlined"
                      size="large"
                      sx={{
                        borderColor: '#1A1A1A',
                        color: '#1A1A1A',
                        px: 4,
                        py: 2,
                        borderRadius: 0,
                        textTransform: 'uppercase',
                        fontWeight: 600,
                        letterSpacing: 1,
                        borderWidth: 2,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: '#FF5722',
                          color: '#FF5722',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 8px 20px rgba(255,87,34,0.2)'
                        }
                      }}
                    >
                      View Examples
                    </Button>
                  </Box>
                </Box>
              </Fade>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Fade in={showElements} timeout={1200}>
                <Box sx={{ position: 'relative' }}>
                  <Box
                    sx={{
                      width: '100%',
                      height: 500,
                      background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                      position: 'relative',
                      overflow: 'hidden',
                      boxShadow: '0 20px 60px rgba(0,0,0,0.1)'
                    }}
                  >
                    <Box
                      component="img"
                      src="/images/3d-viz-hero.jpg"
                      alt="3D Visualization"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        filter: 'brightness(0.9)'
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 30,
                        left: 30,
                        right: 30,
                        background: 'rgba(255,255,255,0.95)',
                        backdropFilter: 'blur(10px)',
                        p: 3,
                        border: '1px solid rgba(255,255,255,0.3)'
                      }}
                    >
                      <Typography 
                        variant="h6" 
                        color="#1A1A1A" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        Photorealistic Rendering
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="#666"
                        sx={{ 
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        Modern Living Room - 4K Resolution
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              color="#999" 
              sx={{ 
                mb: 2, 
                display: 'block',
                fontWeight: 500,
                letterSpacing: 3,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              What We Offer
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              color="#1A1A1A" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Visualization Services
            </Typography>
            <Typography 
              variant="h6" 
              component="p" 
              color="#666" 
              sx={{ 
                maxWidth: 700, 
                mx: 'auto',
                fontWeight: 400,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Cutting-edge 3D technology to visualize your design projects with stunning realism.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} sm={6} lg={4} key={index}>
                <FeatureCard {...feature} delay={200 * (index + 1)} />
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              color="#999" 
              sx={{ 
                mb: 2, 
                display: 'block',
                fontWeight: 500,
                letterSpacing: 3,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Why Choose 3D Visualization
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              color="#1A1A1A" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Benefits That Matter
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Fade in={showElements} style={{ transitionDelay: `${300 * (index + 1)}ms` }}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 4,
                      background: '#FFFFFF',
                      border: '1px solid #E0E0E0',
                      borderRadius: 0,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      height: '100%',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                        borderColor: '#FF5722'
                      }
                    }}
                  >
                    <Typography 
                      variant="h2" 
                      component="div" 
                      sx={{ 
                        fontSize: '3rem',
                        mb: 2
                      }}
                    >
                      {benefit.icon}
                    </Typography>
                    <Typography 
                      variant="h6" 
                      color="#1A1A1A" 
                      gutterBottom
                      sx={{ 
                        fontWeight: 600,
                        mb: 2,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      {benefit.title}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      color="#666"
                      sx={{ 
                        lineHeight: 1.6,
                        fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                      }}
                    >
                      {benefit.description}
                    </Typography>
                  </Box>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#FFFFFF'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="overline" 
              color="#999" 
              sx={{ 
                mb: 2, 
                display: 'block',
                fontWeight: 500,
                letterSpacing: 3,
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Our Specialties
            </Typography>
            <Typography 
              variant="h2" 
              component="h2" 
              color="#1A1A1A" 
              sx={{ 
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
              }}
            >
              Visualization Types
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Fade in={showElements} style={{ transitionDelay: `${400 * (index + 1)}ms` }}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      background: '#FFFFFF',
                      border: '1px solid #E0E0E0',
                      borderRadius: 0,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                        borderColor: '#FF5722'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        height: 250,
                        background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      <Box
                        component="img"
                        src={service.image}
                        alt={service.title}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          filter: 'brightness(0.9)',
                          transition: 'transform 0.3s ease',
                          '&:hover': {
                            transform: 'scale(1.05)'
                          }
                        }}
                      />
                    </Box>
                    <CardContent sx={{ p: 3 }}>
                      <Typography 
                        variant="h5" 
                        component="h3" 
                        color="#1A1A1A" 
                        gutterBottom
                        sx={{ 
                          fontWeight: 600,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {service.title}
                      </Typography>
                      <Typography 
                        variant="body1" 
                        color="#666"
                        sx={{ 
                          lineHeight: 1.6,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {service.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Technology Section */}
      <Box
        sx={{
          py: { xs: 10, md: 15 },
          background: '#F8F8F8'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography 
                variant="overline" 
                color="#999" 
                sx={{ 
                  mb: 2, 
                  display: 'block',
                  fontWeight: 500,
                  letterSpacing: 3,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Advanced Technology
              </Typography>
              <Typography 
                variant="h2" 
                component="h2" 
                color="#1A1A1A" 
                sx={{ 
                  mb: 4,
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Cutting-Edge Tools
              </Typography>
              <Typography 
                variant="body1" 
                color="#666" 
                paragraph
                sx={{ 
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                We use the latest 3D rendering software and virtual reality technology to create 
                visualizations that are indistinguishable from photographs. Our advanced lighting 
                systems and material libraries ensure every detail is perfect.
              </Typography>
              <Typography 
                variant="body1" 
                color="#666" 
                paragraph
                sx={{ 
                  lineHeight: 1.6,
                  fontSize: '1.1rem',
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                From initial concept to final presentation, our 3D visualization services help you 
                communicate your vision clearly and make confident design decisions.
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Grid container spacing={3}>
                {[
                  { number: '4K+', label: 'Ultra HD Quality' },
                  { number: '24hr', label: 'Turnaround Time' },
                  { number: '200+', label: 'Projects Rendered' },
                  { number: '100%', label: 'Client Satisfaction' }
                ].map((stat, index) => (
                  <Grid item xs={6} key={index}>
                    <Box
                      sx={{
                        textAlign: 'center',
                        p: 3,
                        background: '#FFFFFF',
                        border: '1px solid #E0E0E0',
                        borderRadius: 0,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                          borderColor: '#FF5722'
                        }
                      }}
                    >
                      <Typography 
                        variant="h3" 
                        component="div" 
                        sx={{ 
                          color: '#FF5722',
                          fontWeight: 700,
                          mb: 1,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {stat.number}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="#666"
                        sx={{ 
                          fontWeight: 500,
                          fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: 'linear-gradient(135deg, #FF5722, #FF8A65)',
          color: 'white'
        }}
      >
        <Container maxWidth="md">
          <Fade in={showElements}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography 
                variant="h2" 
                component="h2" 
                color="white" 
                sx={{ 
                  mb: 3,
                  fontWeight: 700,
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Bring Your Vision to Life
              </Typography>
              <Typography 
                variant="h5" 
                component="p" 
                color="white" 
                sx={{ 
                  mb: 6, 
                  opacity: 0.9,
                  fontWeight: 400,
                  fontFamily: '"Inter", "Helvetica", "Arial", sans-serif'
                }}
              >
                Experience your design before it's built with our stunning 3D visualizations.
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                <Button
                  component={RouterLink}
                  to="/contact"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowRight />}
                  sx={{
                    background: 'rgba(255,255,255,0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.3)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': { 
                      background: 'rgba(255,255,255,0.3)'
                    }
                  }}
                >
                  Get Quote
                </Button>
                
                <Button
                  component={RouterLink}
                  to="/portfolio"
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: 'rgba(255,255,255,0.5)',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    borderRadius: 0,
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    letterSpacing: 1,
                    fontFamily: '"Inter", "Helvetica", "Arial", sans-serif',
                    '&:hover': {
                      borderColor: 'white',
                      background: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  View Gallery
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </Box>
  );
};

export default ThreeDVisualizationPage;